/**
 * Low-level IndexedDB wrapper with transaction management
 * Follows KISS principle - simple, focused operations
 */

// Note: StorageErrorCode imported but not used - keeping for future error handling
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { devLog, devWarn, devError } from '../utils/devLog';
import { StorageErrorCode } from '../models/storage';

export interface DBConfig {
  name: string;
  version: number;
  stores: {
    name: string;
    keyPath: string;
    indexes?: { name: string; keyPath: string; unique?: boolean }[];
  }[];
}

export interface DBTransaction {
  store: IDBObjectStore;
  transaction: IDBTransaction;
}

/**
 * IndexedDB wrapper class with error handling and transactions
 */
export class IndexedDBWrapper {
  private db: IDBDatabase | null = null;
  private config: DBConfig;

  constructor(config: DBConfig) {
    this.config = config;
  }

  /**
   * Initialize database connection
   */
  async initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.db) {
        resolve();
        return;
      }

      if (!window.indexedDB) {
        reject(new Error('IndexedDB not supported'));
        return;
      }

      const request = indexedDB.open(this.config.name, this.config.version);

      request.onerror = () => {
        reject(new Error(`Failed to open database: ${request.error?.message}`));
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        this.createStores(db);
      };
    });
  }

  /**
   * Create object stores during database upgrade
   */
  private createStores(db: IDBDatabase): void {
    this.config.stores.forEach(storeConfig => {
      // Delete existing store if it exists
      if (db.objectStoreNames.contains(storeConfig.name)) {
        db.deleteObjectStore(storeConfig.name);
      }

      // Create new store
      const store = db.createObjectStore(storeConfig.name, {
        keyPath: storeConfig.keyPath
      });

      // Create indexes
      if (storeConfig.indexes) {
        storeConfig.indexes.forEach(index => {
          store.createIndex(index.name, index.keyPath, {
            unique: index.unique || false
          });
        });
      }

      devLog(`Created object store: ${storeConfig.name}`);
    });
  }

  /**
   * Get a transaction for read operations
   */
  getReadTransaction(storeName: string): DBTransaction {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    const transaction = this.db.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);

    return { store, transaction };
  }

  /**
   * Get a transaction for write operations
   */
  getWriteTransaction(storeName: string): DBTransaction {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    const transaction = this.db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);

    return { store, transaction };
  }

  /**
   * Add or update a record
   */
  async put(storeName: string, data: any): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        const { store, transaction } = this.getWriteTransaction(storeName);
        const request = store.put(data);

        request.onsuccess = () => {
          resolve(request.result as string);
        };

        request.onerror = () => {
          reject(new Error(`Put operation failed: ${request.error?.message}`));
        };

        transaction.onerror = () => {
          reject(new Error(`Transaction failed: ${transaction.error?.message}`));
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Get a record by key
   */
  async get(storeName: string, key: string): Promise<any> {
    return new Promise((resolve, reject) => {
      try {
        const { store, transaction } = this.getReadTransaction(storeName);
        const request = store.get(key);

        request.onsuccess = () => {
          resolve(request.result);
        };

        request.onerror = () => {
          reject(new Error(`Get operation failed: ${request.error?.message}`));
        };

        transaction.onerror = () => {
          reject(new Error(`Transaction failed: ${transaction.error?.message}`));
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Delete a record by key
   */
  async delete(storeName: string, key: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const { store, transaction } = this.getWriteTransaction(storeName);
        const request = store.delete(key);

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          reject(new Error(`Delete operation failed: ${request.error?.message}`));
        };

        transaction.onerror = () => {
          reject(new Error(`Transaction failed: ${transaction.error?.message}`));
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Get all records from a store
   */
  async getAll(storeName: string): Promise<any[]> {
    return new Promise((resolve, reject) => {
      try {
        const { store, transaction } = this.getReadTransaction(storeName);
        const request = store.getAll();

        request.onsuccess = () => {
          resolve(request.result || []);
        };

        request.onerror = () => {
          reject(new Error(`GetAll operation failed: ${request.error?.message}`));
        };

        transaction.onerror = () => {
          reject(new Error(`Transaction failed: ${transaction.error?.message}`));
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Query records using an index
   */
  async getByIndex(storeName: string, indexName: string, value: any): Promise<any[]> {
    return new Promise((resolve, reject) => {
      try {
        const { store, transaction } = this.getReadTransaction(storeName);
        const index = store.index(indexName);
        const request = index.getAll(value);

        request.onsuccess = () => {
          resolve(request.result || []);
        };

        request.onerror = () => {
          reject(new Error(`Index query failed: ${request.error?.message}`));
        };

        transaction.onerror = () => {
          reject(new Error(`Transaction failed: ${transaction.error?.message}`));
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Count records in a store
   */
  async count(storeName: string): Promise<number> {
    return new Promise((resolve, reject) => {
      try {
        const { store, transaction } = this.getReadTransaction(storeName);
        const request = store.count();

        request.onsuccess = () => {
          resolve(request.result || 0);
        };

        request.onerror = () => {
          reject(new Error(`Count operation failed: ${request.error?.message}`));
        };

        transaction.onerror = () => {
          reject(new Error(`Transaction failed: ${transaction.error?.message}`));
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Clear all records from a store
   */
  async clear(storeName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const { store, transaction } = this.getWriteTransaction(storeName);
        const request = store.clear();

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          reject(new Error(`Clear operation failed: ${request.error?.message}`));
        };

        transaction.onerror = () => {
          reject(new Error(`Transaction failed: ${transaction.error?.message}`));
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Close database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
      devLog('IndexedDB connection closed');
    }
  }

  /**
   * Check if database is initialized
   */
  isInitialized(): boolean {
    return this.db !== null;
  }

  /**
   * Get database storage estimate
   */
  async getStorageEstimate(): Promise<{ usage: number; quota: number } | null> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      try {
        const estimate = await navigator.storage.estimate();
        return {
          usage: estimate.usage || 0,
          quota: estimate.quota || 0
        };
      } catch (error) {
      }
    }
    return null;
  }
}
